import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookmarksSimple, Trash, ArrowRight, Lightbulb } from "@phosphor-icons/react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, errMsg } from "@/lib/api";
import { CopyButton } from "@/components/CopyButton";
import { useSeo } from "@/lib/seo";

const FORMAT_COLORS = {
  Tutorial: "var(--glow)", Listicle: "var(--coral)", Challenge: "#F5A623", Story: "var(--success)",
  Comparison: "#4DA6FF", Documentary: "#C77CFF", Reaction: "#FF7AB8",
};

export default function IdeaBoard() {
  useSeo("Idea Board", "Your saved AI video ideas — every brainstorm kept in one place.");
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState(null);

  const load = useCallback(
    () => api.get("/ideas").then((res) => setIdeas(res.data.slice().reverse())),
    []
  );
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const remove = async (id) => {
    await api.delete(`/ideas/${id}`);
    toast.success("Idea removed");
    load();
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 lg:py-14" data-testid="idea-board-page">
      <div className="fade-up mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--coral)] font-semibold mb-2">Never lose a brainstorm</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Idea Board</h1>
        </div>
        <Link
          to="/tools/ai-ideas"
          data-testid="board-generate-more-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--glow)] text-white text-sm font-semibold hover:bg-[#6a4ce6] transition-colors"
        >
          <Lightbulb size={15} weight="bold" /> Generate more ideas
        </Link>
      </div>

      {ideas && ideas.length === 0 && (
        <div className="surface rounded-[20px] p-10 text-center fade-up-1" data-testid="idea-board-empty">
          <BookmarksSimple size={36} color="var(--muted)" weight="duotone" className="mx-auto" />
          <p className="font-bold mt-4">Nothing saved yet</p>
          <p className="text-[var(--muted)] text-sm mt-1.5 max-w-sm mx-auto">
            Run the <Link to="/tools/ai-ideas" className="text-[var(--glow)] hover:underline">AI Idea Generator</Link> and hit Save on any idea worth keeping.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {ideas?.map((idea, i) => (
          <div key={idea.id} className={`surface rounded-[20px] p-6 lift fade-up-${Math.min(i, 3)} group`} data-testid={`board-idea-${idea.id}`}>
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-bold text-lg leading-snug">{idea.title}</h3>
              <div className="flex items-center gap-2 shrink-0">
                {idea.format && (
                  <span
                    className="px-3 py-1 rounded-full text-xs font-bold"
                    style={{
                      background: `color-mix(in srgb, ${FORMAT_COLORS[idea.format] || "var(--glow)"} 14%, transparent)`,
                      color: FORMAT_COLORS[idea.format] || "var(--glow)",
                    }}
                  >
                    {idea.format}
                  </span>
                )}
                <button
                  data-testid={`board-delete-${idea.id}`}
                  onClick={() => remove(idea.id)}
                  className="opacity-70 lg:opacity-0 lg:group-hover:opacity-100 text-[var(--muted)] hover:text-[var(--coral)] transition-opacity"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
            {idea.hook && <p className="text-sm mt-3"><span className="text-[var(--coral)] font-semibold">Hook:</span> {idea.hook}</p>}
            {idea.angle && <p className="text-sm text-[var(--muted)] mt-2">{idea.angle}</p>}
            <p className="text-[10px] text-[var(--muted)] mt-3">
              {idea.niche && <span className="uppercase tracking-[0.15em]">{idea.niche} · </span>}saved {dayjs(idea.createdAt).format("MMM D")}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <CopyButton text={`${idea.title}\n\nHook: ${idea.hook}\n\n${idea.angle}`} label="Copy" testId={`board-copy-${idea.id}`} />
              <button
                data-testid={`board-to-script-${idea.id}`}
                onClick={() => navigate(`/scripts/new?title=${encodeURIComponent(idea.title)}&hook=${encodeURIComponent(idea.hook || "")}`)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--coral)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Start script <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
